import { Clear, Search, AddOutlined as AddOutlinedIcon } from "@mui/icons-material";
import {
	Box,
	Button,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { format, startOfMonth, endOfMonth, subDays, subMonths, subYears, addMonths, parseISO } from "date-fns";
import { useState } from "react";

interface SignatureToolbarProps {
	textSearch?: string;
	onChangeTextSearch?: (value: string) => void;
	onClearTextSearch?: () => void;
	showInputSearch?: boolean;

	showDateType?: boolean;
	onChangeDateType?: (value: string) => void;

	showDateRange?: boolean;
	onChangeDateStart?: (value: string) => void;
	onChangeDateEnd?: (value: string) => void;

	showStatusSelect?: boolean;
	onChangeStatus?: (value: string) => void;

	showRefreshButton?: boolean;
	onClickRefresh?: () => void;

	showGenerateInvoiceButton?: boolean;
	onClickGenerateInvoice?: () => void;

	showNewEnvelopeButton?: boolean;
	onClickNewEnvelope?: () => void;
}

export function SignatureToolbar({
	textSearch = "",
	onChangeTextSearch,
	onClearTextSearch,
	showInputSearch = true,
	showDateRange = true,
	showDateType = true,
	onChangeDateType,
	onChangeDateStart,
	onChangeDateEnd,
	showStatusSelect = false,
	onChangeStatus,
	showRefreshButton = true,
	onClickRefresh,
	showGenerateInvoiceButton = false,
	onClickGenerateInvoice,
	showNewEnvelopeButton = true, // default: mostra o botão
	onClickNewEnvelope, // callback do botão
}: SignatureToolbarProps) {
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
	const today = new Date();

	const [startDate, setStartDate] = useState(format(startOfMonth(today), "yyyy-MM-dd"));
	const [endDate, setEndDate] = useState(format(endOfMonth(today), "yyyy-MM-dd"));
	const [status, setStatus] = useState("todos");
	const [periodType, setPeriodType] = useState("1_mes");
	const [dateType, setDateType] = useState("createdAt");

	const getDateRangeFromPeriod = (period: string): [string, string] => {
		const now = new Date();
		let start: Date = now;
		let end: Date = now;

		switch (period) {
			case "15_dias":
				start = subDays(now, 15);
				end = now;
				break;
			case "1_mes":
				start = startOfMonth(now);
				end = endOfMonth(now);
				break;
			case "2_meses":
				start = startOfMonth(subMonths(now, 1));
				end = endOfMonth(now);
				break;
			case "3_meses":
				start = startOfMonth(subMonths(now, 2));
				end = endOfMonth(now);
				break;
			case "1_ano":
				start = startOfMonth(subYears(now, 1));
				end = endOfMonth(now);
				break;
			case "mes_anterior":
				start = startOfMonth(subMonths(now, 1));
				end = endOfMonth(subMonths(now, 1));
				break;
			case "proximo_mes":
				start = startOfMonth(addMonths(now, 1));
				end = endOfMonth(addMonths(now, 1));
				break;
		}

		return [format(start, "yyyy-MM-dd"), format(end, "yyyy-MM-dd")];
	};

	const handlePeriodChange = (e: SelectChangeEvent) => {
		const value = e.target.value;
		setPeriodType(value);

		if (value !== "personalizado") {
			const [newStart, newEnd] = getDateRangeFromPeriod(value);
			setStartDate(newStart);
			setEndDate(newEnd);
			onChangeDateStart?.(newStart);
			onChangeDateEnd?.(newEnd);
		}
	};

	const handleStatusChange = (e: SelectChangeEvent) => {
		const value = e.target.value;
		setStatus(value);
		onChangeStatus?.(value);
	};

	return (
		<Box component={Paper} display="flex" flexDirection="column" padding={2} gap={1} mx={1}>
			<Grid container spacing={2} direction={isSmallScreen ? "column" : "row"} alignItems={isSmallScreen ? "stretch" : "center"}>
				{/* CONTROLES À ESQUERDA */}
				{showDateType && (
					<Grid item xs={12} sm={6} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel id="date-type-label">Tipo de Data</InputLabel>
							<Select
								labelId="date-type-label"
								value={dateType}
								label="Tipo de Data"
								onChange={(e) => {
									setDateType(e.target.value);
									onChangeDateType?.(e.target.value);
								}}
							>
								<MenuItem value="createdAt">Criação</MenuItem>
								<MenuItem value="deadlineAt">Prazo Final</MenuItem>
							</Select>
						</FormControl>
					</Grid>
				)}

				{showDateRange && (
					<>
						<Grid item xs={12} sm={6} md={2}>
							<FormControl fullWidth size="small">
								<InputLabel id="period-label">Período</InputLabel>
								<Select labelId="period-label" value={periodType} label="Período" onChange={handlePeriodChange}>
									<MenuItem value="15_dias">Últimos 15 dias</MenuItem>
									<MenuItem value="mes_anterior">Mês Anterior</MenuItem>
									<MenuItem value="1_mes">Mês Atual</MenuItem>
									<MenuItem value="proximo_mes">Próximo Mês</MenuItem>
									<MenuItem value="2_meses">Últimos 2 meses</MenuItem>
									<MenuItem value="3_meses">Últimos 3 meses</MenuItem>
									<MenuItem value="1_ano">Último ano</MenuItem>
									<MenuItem value="personalizado">Personalizado</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						{periodType === "personalizado" && (
							<>
								<Grid item xs={12} sm={6} md={2}>
									<TextField
										fullWidth
										size="small"
										type="date"
										label="Data Inicial"
										value={startDate}
										onChange={(e) => {
											setStartDate(e.target.value);
											onChangeDateStart?.(e.target.value);
										}}
									/>
								</Grid>
								<Grid item xs={12} sm={6} md={2}>
									<TextField
										fullWidth
										size="small"
										type="date"
										label="Data Final"
										value={endDate}
										onChange={(e) => {
											setEndDate(e.target.value);
											onChangeDateEnd?.(e.target.value);
										}}
									/>
								</Grid>
							</>
						)}
					</>
				)}

				{showStatusSelect && (
					<Grid item xs={12} sm={6} md={2}>
						<FormControl fullWidth size="small">
							<InputLabel id="status-label">Status</InputLabel>
							<Select labelId="status-label" value={status} label="Status" onChange={handleStatusChange}>
								<MenuItem value="todos">Todos</MenuItem>
								<MenuItem value="running">Em processo</MenuItem>
								<MenuItem value="closed">Finalizado</MenuItem>
								<MenuItem value="canceled">Cancelado</MenuItem>
								<MenuItem value="draft">Rascunho</MenuItem>
							</Select>
						</FormControl>
					</Grid>
				)}

				{showInputSearch && (
					<Grid item xs={12} sm={6} md={3}>
						<TextField
							fullWidth
							size="small"
							placeholder="Pesquisar..."
							value={textSearch}
							onChange={(e) => onChangeTextSearch?.(e.target.value)}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Search />
									</InputAdornment>
								),
								endAdornment: (
									<InputAdornment position="end">
										<IconButton
											onClick={() => {
												onClearTextSearch?.();
												onChangeTextSearch?.("");
											}}
										>
											<Clear />
										</IconButton>
									</InputAdornment>
								),
							}}
						/>
					</Grid>
				)}

				{/* BOTÕES À ESQUERDA (Pesquisar / Faturar) */}
				<Grid item xs={12} sm={12} md>
					<Box display="flex" justifyContent="flex-start" gap={1}>
						{showRefreshButton && (
							<Button variant="contained" onClick={onClickRefresh}>
								Pesquisar
							</Button>
						)}

						{showGenerateInvoiceButton && (
							<Button variant="contained" color="secondary" onClick={onClickGenerateInvoice}>
								Faturar selecionados
							</Button>
						)}
					</Box>
				</Grid>

				{/* BOTÃO À DIREITA: Novo envelope */}
				{showNewEnvelopeButton && (
					<Grid item xs={12} sm="auto" md="auto" sx={{ ml: { md: "auto" } }}>
						<Box display="flex" justifyContent={isSmallScreen ? "flex-start" : "flex-end"}>
							<Button variant="contained" color="primary" startIcon={<AddOutlinedIcon />} onClick={onClickNewEnvelope}>
								Novo envelope
							</Button>
						</Box>
					</Grid>
				)}
			</Grid>

			{showDateRange && (
				<Typography variant="body2" sx={{ mt: 1, ml: 1, fontStyle: "italic", color: "text.secondary" }}>
					Período: {format(parseISO(startDate), "dd/MM/yyyy")} - {format(parseISO(endDate), "dd/MM/yyyy")}
				</Typography>
			)}
		</Box>
	);
}
